import React from 'react';
import { FormGroup } from 'reactstrap';
import { debounce } from 'lodash-es';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { FormCheckbox, FormLabel, toasty } from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { RadioGroup, Radio } from '../common/forms/Radio';
import TimePicker from '../common/forms/TimePicker';

export const EmployeeRestrictionType = {
    Customer: 'customer',
    Employee: 'employee',
    Time: 'time',
};

const RestrictionTypeToId = {
    [EmployeeRestrictionType.Customer]: 1,
    [EmployeeRestrictionType.Employee]: 2,
    [EmployeeRestrictionType.Time]: 3,
};

const RestrictionDays = {
    Sun: 1,
    Mon: 2,
    Tue: 4,
    Wed: 8,
    Thu: 16,
    Fri: 32,
    Sat: 64,
};

export class EmployeeRestriction {
    id = null;

    employeeId = null;

    companyId = null;

    companyName = '';

    foremenId = null;

    foremenName = '';

    blockAll = true;

    restrictedEmployeeId = null;

    restrictedEmployeeName = '';

    restrictionType = EmployeeRestrictionType.Customer;

    employeeRestrictionTypeId = 1;

    restrictionDays = 0;

    restrictionStartTime = null;

    restrictionEndTime = null;
}

export class EmployeeRestrictionForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            restriction: this.props.restriction ?? new EmployeeRestriction(),
            formValidated: false,
            errors: {},
            companies: [],
            foremen: [],
            loading: true,
            saving: false,
            formTitle: 'New Restriction',
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onCompanyChanged = this.onCompanyChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onClose(response) {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onCompanyChanged = async (selectedCompany) => {
        const { restriction } = this.state;

        // Handle null/clear case
        if (!selectedCompany) {
            this.setState({
                foremen: [],
                restriction: {
                    ...restriction,
                    companyId: null,
                    companyName: '',
                    foremenId: null,
                    foremenName: '',
                },
            });
            return;
        }

        try {
            const companyId = selectedCompany.id || selectedCompany.value;
            const companyName = selectedCompany.companyName || selectedCompany.label;
            const foremenData = await httpClient.js(
                ApiRoutes.company.foremen(companyId),
            );

            this.setState({
                foremen: foremenData || [],
                restriction: {
                    ...restriction,
                    companyId,
                    companyName,
                    foremenId: null,
                    foremenName: '',
                },
            });
        } catch (error) {
            console.error('Failed to fetch foremen:', error);
            this.setState((state) => ({
                foremen: [],
                restriction: {
                    ...state.restriction,
                    foremenId: null,
                    foremenName: '',
                },
            }));
        }
    };

    onSubmit = async () => {
        if (this.state.saving) return;
        const { restriction } = this.state;
        const restrictions = this.props.existingRestrictions || [];

        // Validation
        if (restriction.restrictionType === EmployeeRestrictionType.Customer) {
            if (!restriction.companyId) {
                toasty.error('Company selection is required');
                return;
            }

            if (!restriction.blockAll && !restriction.foremenId) {
                toasty.error('Either block all or select a foreman');
                return;
            }
        } else if (restriction.restrictionType === EmployeeRestrictionType.Employee) {
            if (!restriction.restrictedEmployeeId) {
                toasty.error('Employee selection is required');
                return;
            }

            if (restriction.restrictedEmployeeId === restriction.employeeId) {
                toasty.error('Cannot create a restriction against the same employee');
                return;
            }
        } else if (restriction.restrictionType === EmployeeRestrictionType.Time) {
            const hasDays = restriction.restrictionDays && restriction.restrictionDays !== 0;
            const hasTime = restriction.restrictionStartTime && restriction.restrictionEndTime;
            if (!hasDays && !hasTime) {
                toasty.error('Select at least one restricted day or a time range');
                return;
            }
        }

        // Duplicate check
        const isDuplicate = restrictions.some((existing) => {
            if (!existing || !restriction) return false;
            if (existing.id === restriction.id) return false;

            if (restriction.restrictionType === EmployeeRestrictionType.Employee) {
                return existing.restrictedEmployeeId === restriction.restrictedEmployeeId
                    || existing.employeeId === restriction.restrictedEmployeeId;
            }

            if (restriction.restrictionType === EmployeeRestrictionType.Time) {
                return false;
            }

            const companyMatch = existing.companyId === restriction.companyId;

            // Prevent duplicate blockAll restriction for same company
            if (restriction.blockAll) {
                return companyMatch && existing.blockAll;
            }

            // Prevent duplicate foreman restriction for same company/foreman
            return companyMatch && existing.foremenId === restriction.foremenId;
        });

        if (isDuplicate) {
            if (restriction.restrictionType === EmployeeRestrictionType.Employee) {
                toasty.error(`A restriction already exists for ${restriction.restrictedEmployeeName}`);
            } else {
                toasty.error(
                    restriction.blockAll
                        ? `A block all restriction already exists for ${restriction.companyName}`
                        : `A restriction already exists for ${restriction.companyName} with foreman ${restriction.foremenName}`,
                );
            }
            return;
        }

        this.setState({ saving: true, errors: {} });

        try {
            const isNew = !restriction.id;
            const url = isNew
                ? ApiRoutes.employeeRestriction.create()
                : ApiRoutes.employeeRestriction.update();

            const isCustomer = restriction.restrictionType === EmployeeRestrictionType.Customer;
            const isEmployee = restriction.restrictionType === EmployeeRestrictionType.Employee;
            const isTime = restriction.restrictionType === EmployeeRestrictionType.Time;

            const payload = {
                id: restriction.id,
                employeeId: restriction.employeeId,
                employeeRestrictionTypeId: RestrictionTypeToId[restriction.restrictionType],
                companyId: isCustomer ? restriction.companyId : null,
                foremenId: isCustomer ? restriction.foremenId : null,
                blockAll: isCustomer ? restriction.blockAll : false,
                restrictedEmployeeId: isEmployee ? restriction.restrictedEmployeeId : null,
                restrictionDays: isTime ? restriction.restrictionDays : null,
                restrictionStartTime: isTime ? restriction.restrictionStartTime : null,
                restrictionEndTime: isTime ? restriction.restrictionEndTime : null,
            };

            const fetchType = isNew ? httpClient.types.post : httpClient.types.put;

            const response = await httpClient.andGetResponse(
                fetchType,
                url,
                payload,
                `Error ${isNew ? 'creating' : 'updating'} restriction`,
            );

            if (response) {
                const description = isNew ? 'created' : 'updated';
                toasty.success(`Restriction ${description} successfully`);

                if (this.props.onRestrictionChanged) {
                    this.props.onRestrictionChanged();
                }
                this.onClose(response);
            }
        } catch (error) {
            this.handleSaveError(error);
        } finally {
            this.setState({ saving: false });
        }
    };

    onDelete = async () => {
        const { restriction } = this.state;
        if (!restriction.id) {
            toasty.error('Cannot delete an unsaved restriction');
            return;
        }

        this.setState({ saving: true });

        try {
            const url = ApiRoutes.employeeRestriction.delete(restriction.id);
            const response = await httpClient.andGetResponse(
                httpClient.types.delete,
                url,
                null,
                'Error deleting restriction',
                () => this.setState({ saving: false }),
            );

            if (response) {
                toasty.success('Restriction removed successfully');
                if (this.props.onRestrictionChanged) {
                    this.props.onRestrictionChanged();
                }
                this.onClose(response);
            }
        } catch (error) {
            toasty.error('Failed to remove restriction');
            console.error('Error deleting restriction:', error);
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    populateState = async () => {
        try {
            const companies = await httpClient.js(ApiRoutes.typeAheads.customers());
            const { restriction } = this.state;
            let foremen = [];

            if (restriction.companyId) {
                foremen = await httpClient.js(
                    ApiRoutes.company.foremen(restriction.companyId),
                );
            }

            this.setState({
                companies,
                foremen,
                loading: false,
            });
        } catch (error) {
            console.error('Error populating state:', error);
            this.setState({ loading: false });
        }
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
            restriction: new EmployeeRestriction(),
            errors: {},
            foremen: [],
        });
    };

    restrictionExistsInDatabase = () => {
        const { restriction } = this.state;

        return Boolean(restriction && restriction.id);
    };

    open = async (restriction, title = null) => {
        try {
            await this.resetForm();
            let restrictionType;
            if (restriction.employeeRestrictionTypeId === 3) {
                restrictionType = EmployeeRestrictionType.Time;
            } else if (restriction.restrictedEmployeeId) {
                restrictionType = EmployeeRestrictionType.Employee;
            } else {
                restrictionType = EmployeeRestrictionType.Customer;
            }

            let normalizedRestriction = restriction;
            if (
                restrictionType === EmployeeRestrictionType.Employee
                && restriction.restrictedEmployeeId === this.props.employeeId
            ) {
                normalizedRestriction = {
                    ...restriction,
                    employeeId: restriction.restrictedEmployeeId,
                    restrictedEmployeeId: restriction.employeeId,
                    restrictedEmployeeName: restriction.employeeName,
                };
            }
            const currentRestriction = {
                ...normalizedRestriction,
                restrictionType,
            };

            // Set initial form state
            this.setState({
                restriction: currentRestriction,
                formTitle: title || (restriction.id ? 'Edit Restriction' : 'New Restriction'),
                loading: !!currentRestriction.companyId,
            });

            if (currentRestriction.companyId) {
                try {
                    const foremen = await httpClient.js(
                        ApiRoutes.company.foremen(currentRestriction.companyId),
                    );

                    this.setState({
                        foremen: foremen || [],
                        loading: false,
                    });
                } catch (error) {
                    toasty.error('Failed to load foremen for the selected company');
                    console.error('Error loading foremen:', error);
                    this.setState({
                        foremen: [],
                        loading: false,
                    });
                }
            }

            this.context.setFormOpened(true);
        } catch (error) {
            console.error('Error opening restriction form:', error);
            toasty.error('Failed to open restriction form');
            this.setState({ loading: false });
        }
    };

    onRestrictionTypeChanged = (type) => {
        this.setState((state) => {
            let cleared = {};
            if (type === EmployeeRestrictionType.Employee) {
                cleared = {
                    companyId: null, companyName: '', foremenId: null, foremenName: '', blockAll: false, restrictionDays: 0, restrictionStartTime: null, restrictionEndTime: null,
                };
            } else if (type === EmployeeRestrictionType.Time) {
                cleared = {
                    companyId: null, companyName: '', foremenId: null, foremenName: '', blockAll: false, restrictedEmployeeId: null, restrictedEmployeeName: '',
                };
            } else {
                cleared = {
                    restrictedEmployeeId: null, restrictedEmployeeName: '', restrictionDays: 0, restrictionStartTime: null, restrictionEndTime: null,
                };
            }
            return {
                restriction: { ...state.restriction, restrictionType: type, ...cleared },
            };
        });
    };

    loadEmployeeOptions = debounce((inputValue, callback) => {
        if (!inputValue || inputValue.length < 2) {
            callback([]);
            return;
        }

        httpClient.js(ApiRoutes.typeAheads.searchEmployees(inputValue))
            .then((data) => {
                const restrictedEmployeeIds = (this.props.existingRestrictions || [])
                    .filter((restriction) => restriction.id !== this.state.restriction.id)
                    .filter((restriction) => restriction.employeeRestrictionTypeId === RestrictionTypeToId[EmployeeRestrictionType.Employee])
                    .map((restriction) => (
                        restriction.employeeId === this.state.restriction.employeeId
                            ? restriction.restrictedEmployeeId
                            : restriction.employeeId
                    ))
                    .filter(Boolean);
                const restrictedEmployeeIdSet = new Set(restrictedEmployeeIds);
                const options = (data || [])
                    .filter((e) => e.value !== this.state.restriction.employeeId)
                    .filter((e) => !restrictedEmployeeIdSet.has(e.value))
                    .map((item) => ({
                        value: item.value,
                        label: item.label,
                    }));
                callback(options);
            })
            .catch(() => {
                callback([]);
            });
    }, 300);

    onRestrictedEmployeeChanged = (selection) => {
        this.setState((state) => ({
            restriction: {
                ...state.restriction,
                restrictedEmployeeId: selection?.value ?? null,
                restrictedEmployeeName: selection?.label ?? '',
            },
        }));
    };

    onForemenChanged = (selection) => {
        this.setState((state) => ({
            restriction: {
                ...state.restriction,
                foremenId: selection?.id || null,
                foremenName: selection?.fullName || '',
            },
        }));
    };

    onBlockAllChanged = (e) => {
        const blockAll = e.target.checked;
        this.setState((state) => ({
            restriction: {
                ...state.restriction,
                blockAll,
                foremenId: blockAll ? null : state.restriction.foremenId,
                foremenName: blockAll ? '' : state.restriction.foremenName,
            },
        }));
    };

    render() {
        const {
            restriction,
            companies,
            foremen,
            formValidated,
            errors,
            loading,
            saving,
            formTitle,
        } = this.state;

        if (!restriction) {
            return '';
        }

        const selectedForeman = foremen.find((f) => f.id === restriction.foremenId) || null;

        return (
            <SlideForm
                loading={loading}
                show={this.props.show}
                id="employeeRestrictionForm"
                formIcon={faLock}
                formTitle={formTitle}
                ref={this.formRef}
                setIsValidated={(value) => this.setState({ formValidated: value })}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={restriction.id ? this.onDelete : undefined}
                entityId={restriction.id}
                errors={errors}
                saveDisabled={saving}
            >

                <FormGroup className="form-group-input">
                    <FormLabel text="Restriction Type" />
                    <RadioGroup
                        name="restrictionType"
                        value={restriction.restrictionType}
                        onChange={(e) => this.onRestrictionTypeChanged(e.target.value)}
                    >
                        <div className="d-flex flex-row">
                            <Radio
                                value={EmployeeRestrictionType.Customer}
                                label="Customer"
                                formGroupClassName="pr-3"
                            />
                            <Radio
                                value={EmployeeRestrictionType.Employee}
                                label="Employee"
                                formGroupClassName="pr-3"
                            />
                            <Radio
                                value={EmployeeRestrictionType.Time}
                                label="Time"
                                formGroupClassName="pr-3"
                            />
                        </div>
                    </RadioGroup>
                </FormGroup>

                {restriction.restrictionType === EmployeeRestrictionType.Customer && (
                    <>
                        <ValidatedSelect
                            id="customer"
                            name="customer"
                            label="Customer"
                            required
                            options={companies}
                            value={companies.find((c) => c.value === restriction.companyId) || null}
                            onChange={this.onCompanyChanged}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            error="A customer selection is required."
                        />

                        <FormGroup>
                            <div className="ml-3 mt-1 d-flex flex-row align-items-center">
                                <FormCheckbox
                                    id="blockAll"
                                    className="chk-i"
                                    checked={restriction.blockAll}
                                    onChange={this.onBlockAllChanged}
                                    labelText="Block All"
                                    labelClass="pr-2"
                                />
                            </div>
                        </FormGroup>
                        {!restriction.blockAll && (
                            <ValidatedSelect
                                id="foremen"
                                name="foremen"
                                label="Foremen"
                                required
                                options={foremen}
                                value={selectedForeman}
                                onChange={this.onForemenChanged}
                                getOptionLabel={(option) => option.fullName}
                                getOptionValue={(option) => option.id}
                                error="A foremen selection is required."
                            />
                        )}
                    </>
                )}

                {restriction.restrictionType === EmployeeRestrictionType.Employee && (
                    <ValidatedSelect
                        component={AsyncSelect}
                        id="restrictedEmployee"
                        name="restrictedEmployee"
                        label="Restricted Employee"
                        required
                        loadOptions={this.loadEmployeeOptions}
                        placeholder="Type to search employees..."
                        value={
                            restriction.restrictedEmployeeId
                                ? { value: restriction.restrictedEmployeeId, label: restriction.restrictedEmployeeName }
                                : null
                        }
                        onChange={this.onRestrictedEmployeeChanged}
                        isClearable
                        cacheOptions
                        error="An employee selection is required."
                    />
                )}

                {restriction.restrictionType === EmployeeRestrictionType.Time && (
                    <>
                        <FormGroup>
                            <FormLabel text="Restricted Days" />
                            <div className="d-flex flex-row flex-wrap">
                                {Object.entries(RestrictionDays).map(([day, bit]) => (
                                    <FormCheckbox
                                        key={day}
                                        small
                                        id={`restrictionDay-${day}`}
                                        checked={!!(restriction.restrictionDays & bit)}
                                        onChange={() => this.setState((s) => ({
                                            restriction: {
                                                ...s.restriction,
                                                restrictionDays: s.restriction.restrictionDays ^ bit,
                                            },
                                        }))}
                                        labelText={day}
                                        labelClass="pr-3"
                                    />
                                ))}
                            </div>
                            <small className="text-muted d-block mb-2">
                                Restricts the employee from being dispatched on the selected days at all times.
                            </small>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel
                                htmlFor="restrictionStartTime"
                                text="Start Time"
                            />
                            <TimePicker
                                id="restrictionStartTime"
                                name="restrictionStartTime"
                                increment={0.5}
                                value={restriction.restrictionStartTime}
                                onChange={(ev) => {
                                    const v = ev.target.value
                                        ? moment(ev.target.value, 'h:mm A').format('HH:mm')
                                        : null;
                                    this.setState((s) => ({
                                        restriction: { ...s.restriction, restrictionStartTime: v },
                                    }));
                                }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormLabel
                                htmlFor="restrictionEndTime"
                                text="End Time"
                            />
                            <TimePicker
                                id="restrictionEndTime"
                                name="restrictionEndTime"
                                increment={0.5}
                                value={restriction.restrictionEndTime}
                                onChange={(ev) => {
                                    const v = ev.target.value
                                        ? moment(ev.target.value, 'h:mm A').format('HH:mm')
                                        : null;
                                    this.setState((s) => ({
                                        restriction: { ...s.restriction, restrictionEndTime: v },
                                    }));
                                }}
                            />
                        </FormGroup>
                        <small className="text-muted d-block mb-2">
                            Restricts the employee from being dispatched at the selected times for every day.
                        </small>
                    </>
                )}
            </SlideForm>
        );
    }
}
